/**
 * LIFF SDK Hook for React
 */
import { useState, useEffect, useCallback } from 'react'

export interface LiffProfile {
  userId: string
  displayName?: string
  pictureUrl?: string
}

interface LiffInstance {
  init(config: { liffId: string }): Promise<void>
  isLoggedIn(): boolean
  login(options?: { redirectUri?: string }): void
  logout(): void
  getContext(): { userId?: string } | null
  getProfile(): Promise<{ userId?: string; displayName?: string; pictureUrl?: string }>
  getIDToken(): string | null
}

declare global {
  interface Window {
    liff?: LiffInstance
  }
}

let liffLoadPromise: Promise<LiffInstance> | null = null

async function loadLiffSdk(): Promise<LiffInstance> {
  if (typeof window === 'undefined') {
    throw new Error('window is undefined')
  }

  if (window.liff) {
    return window.liff
  }

  if (liffLoadPromise) {
    return liffLoadPromise
  }

  liffLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://static.line-scdn.net/liff/edge/2/sdk.js'
    script.async = true
    script.onload = () => {
      if (window.liff) {
        resolve(window.liff)
      } else {
        liffLoadPromise = null
        reject(new Error('LIFF SDK not available'))
      }
    }
    script.onerror = () => {
      liffLoadPromise = null
      reject(new Error('Failed to load LIFF SDK'))
    }
    document.head.appendChild(script)
  })

  return liffLoadPromise
}

export interface UseLiffOptions {
  liffId: string
  autoLogin?: boolean
}

export interface UseLiffResult {
  isInitialized: boolean
  isLoggedIn: boolean
  isLoading: boolean
  error: string | null
  userId: string | null
  profile: LiffProfile | null
  login: () => void
  logout: () => void
}

export function useLiff({ liffId, autoLogin = true }: UseLiffOptions): UseLiffResult {
  const [isInitialized, setIsInitialized] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  // Start with false - will be set to true when liffId is provided and init starts
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [userId, setUserId] = useState<string | null>(null)
  const [profile, setProfile] = useState<LiffProfile | null>(null)
  const [liff, setLiff] = useState<LiffInstance | null>(null)

  useEffect(() => {
    if (!liffId) {
      // Don't set error immediately - wait for liffId to be provided
      // This allows async fetching of liffId before initialization
      return
    }

    // Reset state when liffId becomes valid
    setError(null)
    setIsLoading(true)

    let cancelled = false

    async function initLiff() {
      try {
        const liffInstance = await loadLiffSdk()
        await liffInstance.init({ liffId })

        if (cancelled) return

        setLiff(liffInstance)
        setIsInitialized(true)

        if (!liffInstance.isLoggedIn()) {
          setIsLoggedIn(false)
          setIsLoading(false)
          if (autoLogin) {
            liffInstance.login({ redirectUri: window.location.href })
          }
          return
        }

        setIsLoggedIn(true)

        // Get user ID
        let uid = ''
        try {
          const context = liffInstance.getContext()
          uid = context?.userId?.trim() || ''
        } catch {
          // Ignore
        }

        if (!uid) {
          try {
            const profileData = await liffInstance.getProfile()
            uid = profileData?.userId?.trim() || ''
            if (profileData) {
              setProfile({
                userId: uid,
                displayName: profileData.displayName,
                pictureUrl: profileData.pictureUrl,
              })
            }
          } catch {
            // Ignore
          }
        }

        if (!uid) {
          try {
            const token = liffInstance.getIDToken()
            if (token) {
              const [, payload] = token.split('.')
              if (payload) {
                const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')))
                uid = decoded?.sub?.trim() || ''
              }
            }
          } catch {
            // Ignore
          }
        }

        if (uid) {
          setUserId(uid)
          try {
            sessionStorage.setItem('user_id', uid)
          } catch {
            // Ignore
          }
        }

        setIsLoading(false)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error')
          setIsLoading(false)
        }
      }
    }

    initLiff()

    return () => {
      cancelled = true
    }
  }, [liffId, autoLogin])

  const login = useCallback(() => {
    if (liff) {
      liff.login({ redirectUri: window.location.href })
    }
  }, [liff])

  const logout = useCallback(() => {
    if (liff) {
      liff.logout()
      setIsLoggedIn(false)
      setUserId(null)
      setProfile(null)
      try {
        sessionStorage.removeItem('user_id')
      } catch {
        // Ignore
      }
    }
  }, [liff])

  return {
    isInitialized,
    isLoggedIn,
    isLoading,
    error,
    userId,
    profile,
    login,
    logout,
  }
}
